import { FC, useEffect } from 'react';

import { useFormikContext } from 'formik';
import { Button } from 'ui/components/common';
import { Input, PhoneNumber, TagsField } from 'ui/components/form';
import useModal from 'ui/partials/Modal/useModal';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';
import { useTour } from '@reactour/tour';
import { TOUR_SELECTOR } from 'ui/partials/Tour';
import { compact } from 'lodash';

import { SIMPLE_LEAD_FORM } from './schema';

type SimpleLeadFormProps = {
  onCancel?: () => void;
};

const SimpleLeadForm: FC<SimpleLeadFormProps> = ({ onCancel }) => {
  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    setValues,
    setFieldError,
    setFieldValue,
    setErrors,
    isSubmitting,
  } = useFormikContext<SIMPLE_LEAD_FORM>();
  const { isOpen } = useTour();

  useEffect(() => {
    if (isOpen) {
      setValues({
        firstName: 'John',
        emailAddress: 'john.smith@leapify.com',
        lastName: 'Smith',
      });
    }
  }, [isOpen, setValues]);

  const { hideModal } = useModal();

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      hideModal();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div
        data-tut={TOUR_SELECTOR.TOUR.CONTACT.LEAD.FORM.GENERAL.FORM}
        className="flex flex-col gap-y-4"
      >
        <Input
          className="w-full"
          type="text"
          name="firstName"
          size={SIZE.Small}
          textType={
            errors.firstName && touched.firstName ? 'danger' : 'default'
          }
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.firstName}
          placeholder="Enter your first name"
          subText={
            errors.firstName && touched.firstName ? errors.firstName : undefined
          }
          label="First Name"
          required
          disabled={isSubmitting}
        />
        <Input
          className="w-full"
          type="text"
          name="lastName"
          size={SIZE.Small}
          textType={errors.lastName && touched.lastName ? 'danger' : 'default'}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.lastName ?? ''}
          placeholder="Enter your last name"
          subText={
            errors.lastName && touched.lastName ? errors.lastName : undefined
          }
          label="Last Name"
          disabled={isSubmitting}
        />
        <Input
          className="w-full"
          type="text"
          name="emailAddress"
          size={SIZE.Small}
          textType={
            errors.emailAddress && touched.emailAddress ? 'danger' : 'default'
          }
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.emailAddress}
          placeholder="Enter your email address"
          subText={
            errors.emailAddress && touched.emailAddress
              ? errors.emailAddress
              : undefined
          }
          required
          label="Email"
          disabled={isSubmitting}
        />
        <PhoneNumber
          className="w-full"
          name="mobileNumber"
          size={SIZE.Medium}
          textType={
            errors.mobileNumber && touched.mobileNumber ? 'danger' : 'default'
          }
          onChange={setFieldValue}
          value={values.mobileNumber ?? ''}
          subText={
            errors.mobileNumber && touched.mobileNumber
              ? errors.mobileNumber
              : undefined
          }
          label="Mobile Number"
          disabled={isSubmitting}
        />
        <TagsField
          className="col-span-4 w-full"
          onChange={(val) =>
            setFieldValue(
              'tagId',
              val?.map((tag) => tag.value),
            )
          }
          defaultValue={compact(values.tagId)}
          setError={(err) => {
            if (err) {
              setFieldError(
                'tagId',
                'Tags cannot have spaces and special characters.',
              );
            } else {
              setErrors({});
            }
          }}
        />
      </div>
      <div
        className={cn(
          'mt-5 pt-5',
          'flex gap-3 justify-end',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          onClick={handleCancel}
          type="button"
          disabled={isSubmitting}
          label="CANCEL"
          intent="tertiary"
          size="sm"
        />
        <Button
          data-tut={TOUR_SELECTOR.TOUR.CONTACT.LEAD.FORM.GENERAL.SAVE_BUTTON}
          disabled={isSubmitting}
          label="SAVE"
          type="submit"
          size="sm"
        />
      </div>
    </form>
  );
};

export default SimpleLeadForm;
